/* How far a square's start is from the booking behind it.
 *
 * A booked square usually starts exactly when the book says — see
 * `bookedOutcome` in the store, which backdates a late seating to the booked
 * time. It does not when the booking system says when the visit's first
 * service was started (js/rules/seated-start.js — a client running late shows
 * here), when somebody was put up early, when the desk said "start
 * it now" on the overrun question (js/rules/overrun.js), when a walk-in square
 * was attached to a booking afterwards, or when the start was edited by hand.
 * Those are the squares this marks, and only past a margin the salon sets:
 * a client seated two minutes early is the ordinary case, not a fact.
 *
 * The booked start is read off the book every time rather than snapshotted
 * onto the turn. A booking moved in the booking system to match when the
 * client actually sat down is the desk correcting the record, and the mark
 * has to go away when they do.
 *
 * This file imports nothing, for the same reason as js/rules/conflict.js.
 */

export var OFFSET_DEFAULT_MIN = 5;
export var OFFSET_LIMITS = { min: 1, max: 60 };

// Whatever is stored, as minutes the board can use. Unset or junk is the
// default; out of range is clamped, the same as the conflict buffer.
export function cleanOffsetMinutes(value) {
  if (value === '' || value == null) return OFFSET_DEFAULT_MIN;
  var n = Number(value);
  if (!isFinite(n)) return OFFSET_DEFAULT_MIN;
  return Math.min(OFFSET_LIMITS.max, Math.max(OFFSET_LIMITS.min, Math.round(n)));
}

/* Started minus booked, in milliseconds: positive is late, negative is early.
 * Null when either end is missing — a square with no booking has nothing to be
 * early or late for. */
export function startOffsetMs(startedAt, bookedStartsAt) {
  if (startedAt == null || bookedStartsAt == null) return null;
  return startedAt - bookedStartsAt;
}

/* The mark, or null for "within the margin".
 *
 * More than the margin, not equal to it: five minutes is the salon saying five
 * minutes either way is on time. */
export function startOffset(startedAt, bookedStartsAt, marginMinutes) {
  var diff = startOffsetMs(startedAt, bookedStartsAt);
  if (diff == null) return null;
  var margin = cleanOffsetMinutes(marginMinutes) * 60000;
  if (Math.abs(diff) <= margin) return null;
  return {
    kind: diff > 0 ? 'late' : 'early',
    ms: Math.abs(diff),
    bookedAt: bookedStartsAt,
    startedAt: startedAt
  };
}

/* How much of the bar the offset is, 0 to 100, against the length the bar is
 * drawn over. Null with no length — there is no bar to take a share of. Capped
 * at the whole bar: a square started an hour late on a thirty minute booking
 * is all red, not red off the edge. */
export function offsetPct(ms, scheduledMinutes) {
  if (ms == null || !scheduledMinutes) return null;
  var pct = ms / (scheduledMinutes * 60000);
  return Math.round(Math.max(0, Math.min(1, pct)) * 10000) / 100;
}
